// Custom hook for package matching logic
import { useMemo } from "react";
import type { ChannelCardV2, PackageSummary } from "lib/helper/channel-interactive-tool-v2";
import { matchShowPackages, normalizeForMatch } from "lib/helper/channel-interactive-tool-v2/package-matching";
import type { ShowData, PackageMatchResult } from "./types";

// Re-exported for MovieModalV2.tsx — the implementation moved to the server-safe
// shared module (lib/helper/channel-interactive-tool-v2/package-matching.ts, WAT-11211)
// so the catalog proxy runs the SAME join server-side.
export { normalizeForMatch };

/**
 * Convert server-computed catalog enrichment (WAT-11211) riding on the show object
 * into the modal's PackageMatchResult shape. Returns null (→ client match runs) when:
 *  - the show carries no server enrichment at all, or
 *  - the enrichment is empty (the server's channel cache was cold — indistinguishable
 *    from a genuine no-match; the client match gives the same answer when truly
 *    unmatched and rescues the modal when the cache wasn't warm), or
 *  - none of the server refs resolve against THIS page's packageSummaries — the server
 *    computes against its first-warm channel-map variant, so a satellite page (DTV_*
 *    summaries) must not trust refs computed from the internet map (ATT_TV_*).
 */
const serverMatchResult = (
  show: ShowData | null,
  packageSummaries: Record<string, PackageSummary>
): PackageMatchResult | null => {
  if (!show?.packages && !show?.channels) {
    return null;
  }
  const signaturePackages = show.packages?.signature || [];
  const genrePackages = show.packages?.genre || [];
  const result: PackageMatchResult = {
    signaturePackages,
    genrePackages,
    // union entries carry their networks inline — rebuild the modal's pkgId→networks map
    packageNetworkMap: Object.fromEntries(
      [...signaturePackages, ...genrePackages]
        .filter(ref => ref?.networks?.length)
        .map(ref => [ref.id, ref.networks])
    ),
    matchedChannelLabels: (show.channels || []).map(channel => channel?.label)
  };
  const resolvable = result.signaturePackages.some(ref => packageSummaries?.[ref?.id]) ||
    result.genrePackages.some(ref => packageSummaries?.[ref?.id]);
  return resolvable ? result : null;
};

/**
 * Custom hook for matching shows with packages.
 * Prefers the catalog's server-computed enrichment when the show carries it
 * (search-enriched movies, detail-enriched series); falls back to the shared
 * client-side match (package-matching.ts) otherwise. packageSummaries/chnlSrc/
 * genreConfig stay in the dependency array to preserve the original memo
 * invalidation behavior.
 */
export const usePackageMatching = (
  show: ShowData | null,
  channelMap: Record<string, ChannelCardV2>,
  packageSummaries: Record<string, PackageSummary>,
  ovChnlPageData: any,
  chnlSrc: string,
  genreConfig: any
): PackageMatchResult => useMemo(
  () => serverMatchResult(show, packageSummaries) ?? matchShowPackages(show, channelMap, ovChnlPageData),
  [show, channelMap, packageSummaries, ovChnlPageData, chnlSrc, genreConfig]
);

/**
 * Extract network name for display (fallback when no packages found)
 */
export const extractDisplayNetworkName = (show: ShowData | null): string => {
  if (!show) {
    return "";
  }

  return show?.networkAttributionName ||
    show?.consumables?.[0]?.provider?.networkName ||
    show?.consumables?.[0]?.channel?.networkName ||
    show?.consumables?.[0]?.channel?.callSign ||
    show?.consumables?.[0]?.channel?.name ||
    "";
};
